import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import {
  TutorCodeDTO,
  TutorCodeGenerateRequest,
  activateCode,
  deactivateCode,
  deleteCode,
  generateCode,
  listCodes,
} from '../services/tutorCodeService';
import { getCourse, TutorCourse } from '../services/tutorCourseService';

const STATUS_COLORS: Record<string, 'success' | 'default' | 'warning' | 'error'> = {
  ACTIVE: 'success',
  INACTIVE: 'default',
  EXPIRED: 'warning',
  DEPLETED: 'error',
};

export default function TutorCourseCodes() {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const id = Number(courseId);

  const [codes, setCodes] = useState<TutorCodeDTO[]>([]);
  const [course, setCourse] = useState<TutorCourse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<string | null>(null);

  const [generateOpen, setGenerateOpen] = useState(false);
  const [maxUses, setMaxUses] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [generating, setGenerating] = useState(false);

  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  const load = useCallback(() => {
    if (!id) return;
    setLoading(true);
    Promise.all([listCodes(id), getCourse(id)])
      .then(([codesData, courseData]) => {
        setCodes(codesData);
        setCourse(courseData);
      })
      .catch(() => setError('Failed to load registration codes.'))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleGenerate = async () => {
    setGenerating(true);
    const payload: TutorCodeGenerateRequest = {
      maxUses: maxUses ? Number(maxUses) : null,
      expiresAt: expiresAt || null,
    };
    try {
      const newCode = await generateCode(id, payload);
      setCodes((prev) => [newCode, ...prev]);
      setGenerateOpen(false);
      setMaxUses('');
      setExpiresAt('');
      setSnackbar('Code generated successfully');
    } catch {
      setSnackbar('Failed to generate code. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleDeactivate = async (code: TutorCodeDTO) => {
    try {
      const updated = await deactivateCode(id, code.id);
      setCodes((prev) => prev.map((c) => (c.id === code.id ? updated : c)));
    } catch {
      setSnackbar('Failed to deactivate code.');
    }
  };

  const handleActivate = async (code: TutorCodeDTO) => {
    try {
      const updated = await activateCode(id, code.id);
      setCodes((prev) => prev.map((c) => (c.id === code.id ? updated : c)));
    } catch {
      setSnackbar('Failed to activate code.');
    }
  };

  const handleDelete = async () => {
    if (confirmDeleteId === null) return;
    try {
      await deleteCode(id, confirmDeleteId);
      setCodes((prev) => prev.filter((c) => c.id !== confirmDeleteId));
      setSnackbar('Code deleted.');
    } catch {
      setSnackbar('Failed to delete code.');
    } finally {
      setConfirmDeleteId(null);
    }
  };

  const handleCopy = (code: string) => {
    navigator.clipboard.writeText(code);
    setSnackbar(`Copied: ${code}`);
  };

  const formatUses = (c: TutorCodeDTO) =>
    c.maxUses != null ? `${c.currentUses} / ${c.maxUses}` : `${c.currentUses} / unlimited`;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={() => navigate('/tutor/courses')} sx={{ mt: 2 }}>
          Back to courses
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link underline="hover" sx={{ cursor: 'pointer' }} onClick={() => navigate('/tutor/courses')}>
          My Courses
        </Link>
        {course && (
          <Link underline="hover" sx={{ cursor: 'pointer' }} onClick={() => navigate(`/tutor/courses/${id}`)}>
            {course.name}
          </Link>
        )}
        <Typography color="text.primary">Registration Codes</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Registration Codes
          </Typography>
          {course && (
            <Typography variant="body2" color="text.secondary">{course.name}</Typography>
          )}
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setGenerateOpen(true)}>
          Generate code
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Share these codes with students so they can self-enroll in your course.
      </Typography>

      {codes.length === 0 ? (
        <Alert severity="info">No registration codes yet. Generate one to share with students.</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Uses</TableCell>
                <TableCell>Expires</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {codes.map((c) => (
                <TableRow key={c.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        fontFamily="monospace"
                        fontWeight={600}
                        fontSize={15}
                        letterSpacing={2}
                      >
                        {c.code}
                      </Typography>
                      <Tooltip title="Copy code">
                        <IconButton size="small" onClick={() => handleCopy(c.code)}>
                          <ContentCopyIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell>{formatUses(c)}</TableCell>
                  <TableCell>{c.expiresAt ?? '—'}</TableCell>
                  <TableCell>
                    <Chip
                      label={c.status}
                      color={STATUS_COLORS[c.status] ?? 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                      {c.status === 'ACTIVE' ? (
                        <Tooltip title="Deactivate">
                          <IconButton size="small" onClick={() => handleDeactivate(c)}>
                            <PauseCircleIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Activate">
                          <IconButton size="small" onClick={() => handleActivate(c)}>
                            <PlayCircleIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => setConfirmDeleteId(c.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Generate dialog */}
      <Dialog open={generateOpen} onClose={() => setGenerateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Generate registration code</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField
            label="Max uses (leave blank for unlimited)"
            type="number"
            value={maxUses}
            onChange={(e) => setMaxUses(e.target.value)}
            inputProps={{ min: 1 }}
            fullWidth
          />
          <TextField
            label="Expiry date (leave blank for no expiry)"
            type="date"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleGenerate} disabled={generating}>
            {generating ? 'Generating…' : 'Generate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm dialog */}
      <Dialog open={confirmDeleteId !== null} onClose={() => setConfirmDeleteId(null)}>
        <DialogTitle>Delete this code?</DialogTitle>
        <DialogContent>
          <Typography>
            Students who already enrolled using this code will keep their access. New enrollments
            will no longer be accepted.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteId(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar !== null}
        autoHideDuration={3000}
        onClose={() => setSnackbar(null)}
        message={snackbar}
      />
    </Box>
  );
}
